import { Link } from 'react-router-dom';
import LegalLayout from './LegalLayout';

export default function CookiePolicy() {
  return (
    <LegalLayout
      title="Cookie Policy"
      subtitle="How Veridex uses cookies and similar technologies — and how you stay in control."
      lastUpdated="19 April 2026"
    >
      <h2>1. What are cookies?</h2>
      <p>
        Cookies are small text files stored in your browser when you visit a website. Similar
        technologies include localStorage, sessionStorage and IndexedDB. Together we call
        these "cookies" in this Policy.
      </p>

      <h2>2. Categories we use</h2>

      <h3>Strictly Necessary</h3>
      <p>
        Required to run the Service. They cannot be disabled. Examples:
      </p>
      <ul>
        <li><code>access_token</code> — authenticated session (JWT, HttpOnly, Secure).</li>
        <li><code>csrf_token</code> — CSRF double-submit token (readable, Secure).</li>
        <li><code>cp_user</code> — cached user profile for the current session.</li>
        <li><code>cp_language</code> — selected UI language.</li>
        <li><code>veridex-theme</code> — light / dark preference.</li>
        <li><code>cc_cookie</code> — records your cookie decision so the banner does not reappear.</li>
      </ul>
      <p className="text-xs text-muted-foreground">
        Legal basis: Art. 6(1)(b) GDPR (necessary for performance of the contract).
      </p>

      <h3>Analytics (optional)</h3>
      <p>
        Loaded only if you click <em>Accept</em> in the consent banner. Examples:
      </p>
      <ul>
        <li><strong>Google Analytics 4</strong> — pseudonymous product usage, funnel conversion, feature adoption.</li>
        <li><strong>Sentry</strong> — browser error telemetry (production only).</li>
      </ul>

      <h3>Marketing &amp; Support (optional)</h3>
      <ul>
        <li><strong>HubSpot</strong> — CRM contact cookie, form attribution.</li>
        <li><strong>Tawk.to</strong> — in-product live-chat widget state.</li>
      </ul>

      <h3>Preferences</h3>
      <p>
        Remember local choices like language and theme. These live in your browser's
        localStorage and are never transmitted to third parties.
      </p>

      <h2>3. Your control</h2>
      <p>
        On your first visit, a banner at the bottom of the screen asks for consent to the
        optional categories. You can change your choice at any time:
      </p>
      <ul>
        <li>
          Open <Link to="/settings">Settings</Link> &rarr; <strong>Cookie preferences</strong> &rarr; <em>Change preferences</em>.
          This clears the stored decision and the banner reappears on the next page load.
        </li>
        <li>
          Alternatively, clear cookies for this site in your browser settings — the banner
          will reappear on the next visit.
        </li>
      </ul>

      <h2>4. Do Not Track</h2>
      <p>
        If your browser sends a <code>Do Not Track</code> signal, Veridex treats it as a
        rejection of optional cookies. No analytics or marketing cookies are loaded.
      </p>

      <h2>5. Retention</h2>
      <p>
        Consent decisions are stored for twelve (12) months. After that period we ask
        again. Session cookies are cleared on browser close; persistent cookies retain
        the lifetime declared by the issuer (mostly 13 months maximum).
      </p>

      <h2>6. More information</h2>
      <p>
        For the full picture of data we collect and how we use it, see the{' '}
        <Link to="/legal/privacy">Privacy Policy</Link>. Questions:{' '}
        <a href="mailto:privacy@veridex-one.com">privacy@veridex-one.com</a>.
      </p>
    </LegalLayout>
  );
}
